import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app-shell";
import { formatCurrency } from "@/lib/format";
import { useActiveCompany } from "@/hooks/use-active-company";
import { getCapabilities } from "@/modules/capabilities";
import type { ModuleKey } from "@/modules/contract";
import { projectsLabel } from "@/modules/navigation";
import { DASHBOARD_KPI_LINKS } from "@/lib/dashboard-links";
import type { QuoteView } from "@/lib/quote-views";
import {
  FileText,
  Users,
  FolderKanban,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle2,
  Send,
  Activity,
  Camera,
  ClipboardCheck,

} from "lucide-react";
import { QUOTE_STATUS_LABELS, QUOTE_STATUS_CLASSES, type QuoteStatus } from "@/lib/calc";
import {
  OPEN_STATUSES,
  SENT_STATUSES,
  WON_STATUSES,
  THIRTY_DAYS_MS,
} from "@/lib/quote-views";
import { currentRevisions } from "@/lib/quote-revisions";


export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard | VERQO" },
      { name: "description", content: "Oversikt over prosjekter, tilbud, omsetning og aktivitet." },
    ],
  }),
  component: DashboardPage,
});

const WON = WON_STATUSES;
const OPEN = OPEN_STATUSES;
const SENT = SENT_STATUSES;


function relTime(iso: string): string {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const min = Math.round(diff / 60000);
  if (min < 1) return "nå";
  if (min < 60) return `${min} min siden`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} t siden`;
  const days = Math.round(h / 24);
  if (days < 7) return `${days} d siden`;
  return new Date(iso).toLocaleDateString("nb-NO");
}

function DashboardPage() {
  const { activeModuleKey, companyId } = useActiveCompany();
  const caps = getCapabilities(activeModuleKey);

  if (!caps.hasQuotes) {
    return <SurveyorDashboard moduleKey={activeModuleKey} label={projectsLabel(activeModuleKey)} />;
  }
  return <TradeDashboard moduleKey={activeModuleKey} companyId={companyId} />;
}


/**
 * Elektro/tømrer: prosjekt- og tilbudsdrevet oversikt, alltid filtrert
 * på aktiv modul slik at fagene ikke ser hverandres data.
 */
function TradeDashboard({ moduleKey, companyId }: { moduleKey: ModuleKey; companyId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard-v2", companyId, moduleKey],
    enabled: !!companyId,
    queryFn: async () => {
      const [customers, projects, quotes, recentQuotes, events, upcomingVisits] = await Promise.all(
        [
          supabase
            .from("customers")
            .select("*", { count: "exact", head: true })
            .eq("company_id", companyId),
          supabase
            .from("projects")
            .select("id,name,project_number,status,updated_at")
            .eq("company_id", companyId)
            .eq("module_key", moduleKey),
          supabase
            .from("quotes")
            .select("id,revision_of,revision_label,status,total_incl_vat,created_at,approved_at")
            .eq("company_id", companyId)
            .eq("module_key", moduleKey),
          supabase
            .from("quotes")
            .select(
              "id,quote_number,title,status,total_incl_vat,created_at,revision_label,project_number,customers(name)",
            )
            .eq("company_id", companyId)
            .eq("module_key", moduleKey)
            .order("created_at", { ascending: false })
            .limit(5),
          supabase
            .from("project_events")
            .select(
              "id,project_id,event_type,title,description,created_at,projects!inner(module_key)",
            )
            .eq("projects.module_key", moduleKey)
            .order("created_at", { ascending: false })
            .limit(6),
          supabase
            .from("site_visits")
            .select("id,project_id,created_at,status,note,projects!inner(name,module_key)")
            .eq("projects.module_key", moduleKey)
            .order("created_at", { ascending: false })
            .limit(5),
        ],
      );

      // Ingen falsk tomtilstand: en feilet spørring skal vises som feil.
      const firstError =
        customers.error ??
        projects.error ??
        quotes.error ??
        recentQuotes.error ??
        events.error ??
        upcomingVisits.error;
      if (firstError) throw new Error(firstError.message);

      // Kun gjeldende revisjon telles, ellers dobbelttelles erstattede tilbud.
      const qs = currentRevisions(quotes.data ?? []);
      const ps = projects.data ?? [];
      const now = Date.now();
      const monthAgo = now - THIRTY_DAYS_MS;

      return {
        customerCount: customers.count ?? 0,
        activeProjects: ps.filter(
          (p) => (p.status ?? "aktivt") !== "avsluttet" && (p.status ?? "aktivt") !== "arkivert",
        ).length,
        openQuotes: qs.filter((q) => OPEN.includes(q.status as QuoteStatus)).length,
        sentQuotes: qs.filter((q) => SENT.includes(q.status as QuoteStatus)).length,
        approvedCount: qs.filter((q) => q.status === "approved").length,
        wonValue: qs
          .filter((q) => WON.includes(q.status as QuoteStatus))
          .reduce((s, q) => s + Number(q.total_incl_vat ?? 0), 0),
        wonValue30d: qs
          .filter((q) => WON.includes(q.status as QuoteStatus))
          .filter((q) => new Date(q.approved_at ?? q.created_at).getTime() >= monthAgo)
          .reduce((s, q) => s + Number(q.total_incl_vat ?? 0), 0),
        pipelineValue: qs
          .filter((q) => SENT.includes(q.status as QuoteStatus))
          .reduce((s, q) => s + Number(q.total_incl_vat ?? 0), 0),
        recent: recentQuotes.data ?? [],
        events: events.data ?? [],
        upcoming: upcomingVisits.data ?? [],
      };
    },
  });


  const stats = [
    {
      label: "Aktive prosjekter",
      value: data?.activeProjects ?? 0,
      icon: FolderKanban,
      link: DASHBOARD_KPI_LINKS.activeProjects,
      tone: "text-blue-600 dark:text-blue-400",
    },
    {
      label: "Åpne tilbud",
      value: data?.openQuotes ?? 0,
      icon: FileText,
      link: DASHBOARD_KPI_LINKS.openQuotes,
      tone: "text-amber-600 dark:text-amber-400",
    },
    {
      label: "Godkjente tilbud",
      value: data?.approvedCount ?? 0,
      icon: CheckCircle2,
      link: DASHBOARD_KPI_LINKS.approvedQuotes,
      tone: "text-emerald-600 dark:text-emerald-400",
    },
    {
      label: "Omsetning (godkjent)",
      value: formatCurrency(data?.wonValue ?? 0),
      icon: TrendingUp,
      link: DASHBOARD_KPI_LINKS.wonValue,
      tone: "text-primary",
    },
  ];

  return (
    <>
      <PageHeader
        title="Oversikt"
        subtitle="Velkommen tilbake."
        action={
          <Link to="/quotes" className="btn btn-primary">
            <Plus className="h-4 w-4" /> Nytt tilbud
          </Link>
        }
      />

      {error && (
        <div className="card mb-4 border-destructive/40 p-4 text-sm text-destructive">
          Kunne ikke hente oversikten: {(error as Error).message}
        </div>
      )}

      {/* KPI-kort */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((s) => (
          <Link
            key={s.label}
            {...(s.link as { to: "/quotes"; search?: { view: QuoteView } })}
            className="block"
          >
            <div className="card p-4 transition-shadow hover:shadow-md active:opacity-80">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.tone}`} />
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-tight">{s.value}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Sekundære KPI-er */}
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Link to="/customers" className="block">
          <div className="card p-4 transition-shadow hover:shadow-md active:opacity-80">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Kunder totalt</span>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-2 text-xl font-semibold">{data?.customerCount ?? 0}</div>
          </div>
        </Link>
        <Link to="/quotes" search={{ view: "sent" }} className="block">
          <div className="card p-4 transition-shadow hover:shadow-md active:opacity-80">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Sendt (venter svar)</span>
              <Send className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-2 text-xl font-semibold">{data?.sentQuotes ?? 0}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Pipeline: {formatCurrency(data?.pipelineValue ?? 0)}
            </div>
          </div>
        </Link>
        <Link to="/quotes" search={{ view: "won-30d" }} className="block">
          <div className="card p-4 transition-shadow hover:shadow-md active:opacity-80">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Omsetning siste 30 dager
              </span>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-2 text-xl font-semibold">{formatCurrency(data?.wonValue30d ?? 0)}</div>
          </div>
        </Link>
      </div>


      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Siste tilbud */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Siste tilbud</h2>
            <Link to="/quotes" className="text-sm font-medium text-primary hover:underline">
              Se alle <ArrowRight className="ml-0.5 inline h-3.5 w-3.5" />
            </Link>
          </div>
          {!isLoading && data && data.recent.length === 0 ? (
            <div className="card p-8 text-center text-sm text-muted-foreground">
              Ingen tilbud ennå.{" "}
              <Link to="/quotes" className="font-medium text-primary hover:underline">
                Lag ditt første →
              </Link>
            </div>
          ) : (
            <div className="card divide-y divide-border">
              {data?.recent.map((q) => {
                const status = q.status as QuoteStatus;
                const label = QUOTE_STATUS_LABELS[status] ?? status;
                const cls = QUOTE_STATUS_CLASSES[status] ?? QUOTE_STATUS_CLASSES.draft;
                const num =
                  q.project_number ??
                  `${q.quote_number}${q.revision_label ? ` Rev ${q.revision_label}` : ""}`;
                return (
                  <Link
                    key={q.id}
                    to="/quotes/$id"
                    params={{ id: q.id }}
                    className="flex items-center justify-between px-4 py-3 hover:bg-muted"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">{num}</span>
                        <span className={`chip ${cls}`}>{label}</span>
                      </div>
                      <div className="mt-0.5 truncate text-sm font-medium">{q.title}</div>
                      {q.customers && (
                        <div className="truncate text-xs text-muted-foreground">
                          {(q.customers as { name: string }).name}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-sm font-semibold">
                      {formatCurrency(q.total_incl_vat)}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Siste aktivitet */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Siste aktivitet</h2>
            <Link to="/projects" className="text-sm font-medium text-primary hover:underline">
              Prosjekter <ArrowRight className="ml-0.5 inline h-3.5 w-3.5" />
            </Link>
          </div>
          {!isLoading && data && data.events.length === 0 ? (
            <div className="card p-8 text-center text-sm text-muted-foreground">
              Ingen aktivitet registrert enda.
            </div>
          ) : (
            <div className="card divide-y divide-border">
              {data?.events.map((e) => (
                <Link
                  key={e.id}
                  to="/projects/$id"
                  params={{ id: e.project_id }}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted"
                >
                  <Activity className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{e.title}</div>
                    {e.description && (
                      <div className="truncate text-xs text-muted-foreground">{e.description}</div>
                    )}
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {relTime(e.created_at)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Kommende oppgaver: befaringer */}
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Siste befaringer</h2>
        </div>
        {!isLoading && data && data.upcoming.length === 0 ? (
          <div className="card p-8 text-center text-sm text-muted-foreground">
            Ingen befaringer registrert enda.
          </div>
        ) : (
          <div className="card divide-y divide-border">
            {data?.upcoming.map((v) => (
              <Link
                key={v.id}
                to="/projects/$id/visit"
                params={{ id: v.project_id }}
                className="flex items-center justify-between px-4 py-3 hover:bg-muted"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {(v.projects as { name?: string } | null)?.name ?? "Prosjekt"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(v.created_at).toLocaleString("nb-NO", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </div>
                  </div>
                </div>
                <span className="chip">{v.status ?? "planlagt"}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Takst: ingen tilbud, omsetning eller faktura. Kun oppdrag, befaringer,
 * bilder og aktivitet fra dagens skjema, filtrert på building_surveyor.
 * «Utstedte rapporter» vises ikke før Takst-skjemaet faktisk er i drift.
 */
function SurveyorDashboard({ moduleKey, label }: { moduleKey: ModuleKey; label: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-survey", moduleKey],
    queryFn: async () => {
      const [customers, projects, assignmentCount, visits, visitCount, imageCount, events] =
        await Promise.all([
          supabase.from("customers").select("*", { count: "exact", head: true }),
          supabase
            .from("projects")
            .select("id,name,project_number,status,updated_at")
            .eq("module_key", moduleKey),
          // Faktiske takstoppdrag – aldri utledet fra prosjekter.
          supabase.from("takst_assignments").select("id", { count: "exact", head: true }),
          supabase
            .from("site_visits")
            .select("id,project_id,created_at,status,note,projects!inner(name,module_key)")
            .eq("projects.module_key", moduleKey)
            .order("created_at", { ascending: false })
            .limit(5),
          // Egen teller: listen over er begrenset til 5 og kan aldri telle.
          supabase
            .from("site_visits")
            .select("id,projects!inner(module_key)", { count: "exact", head: true })
            .eq("projects.module_key", moduleKey),
          supabase
            .from("project_images")
            .select("id,projects!inner(module_key)", { count: "exact", head: true })
            .eq("projects.module_key", moduleKey),
          supabase
            .from("project_events")
            .select("id,project_id,title,description,created_at,projects!inner(module_key)")
            .eq("projects.module_key", moduleKey)
            .order("created_at", { ascending: false })
            .limit(6),
        ]);

      const ps = projects.data ?? [];
      return {
        customerCount: customers.count ?? 0,
        activeProjects: ps.filter(
          (p) => (p.status ?? "aktivt") !== "avsluttet" && (p.status ?? "aktivt") !== "arkivert",
        ).length,
        assignmentCount: assignmentCount.count ?? 0,
        visits: visits.data ?? [],
        visitCount: visitCount.count ?? 0,
        imageCount: imageCount.count ?? 0,
        events: events.data ?? [],
      };
    },
  });

  const isSurveyor = moduleKey === "building_surveyor";

  const stats = [
    {
      label: `Aktive ${label.toLowerCase()}`,
      value: data?.activeProjects ?? 0,
      icon: FolderKanban,
      to: "/projects" as const,
    },
    {
      label: "Takstoppdrag",
      value: data?.assignmentCount ?? 0,
      icon: ClipboardCheck,
      to: "/takst" as const,
    },
    { label: "Prosjektbefaringer", value: data?.visitCount ?? 0, icon: Clock },
    { label: "Bilder i grunnlaget", value: data?.imageCount ?? 0, icon: Camera },
    { label: "Kunder totalt", value: data?.customerCount ?? 0, icon: Users },
  ];

  return (
    <>
      <PageHeader
        title="Oversikt"
        subtitle="Takstoppdrag, prosjektbefaring og rapportgrunnlag."
        action={
          isSurveyor ? (
            <Link to="/takst" className="btn btn-primary">
              <Plus className="h-4 w-4" /> Nytt takstoppdrag
            </Link>
          ) : (
            <Link to="/projects" className="btn btn-primary">
              <Plus className="h-4 w-4" /> Nytt oppdrag
            </Link>
          )
        }
      />


      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {stats.map((s) => {
          const Body = (
            <div className="card p-4 transition-shadow hover:shadow-md">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
                <s.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-tight">{s.value}</div>
            </div>
          );
          return s.to ? (
            <Link key={s.label} to={s.to}>
              {Body}
            </Link>
          ) : (
            <div key={s.label}>{Body}</div>
          );
        })}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-3 text-lg font-semibold">Siste prosjektbefaringer</h2>
          {!isLoading && data && data.visits.length === 0 ? (
            <div className="card p-8 text-center text-sm text-muted-foreground">
              Ingen prosjektbefaringer registrert enda.
            </div>
          ) : (
            <div className="card divide-y divide-border">
              {data?.visits.map((v) => (
                <Link
                  key={v.id}
                  to={isSurveyor ? "/projects/$id" : "/projects/$id/visit"}
                  params={{ id: v.project_id }}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {(v.projects as { name?: string } | null)?.name ?? "Prosjekt"}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {new Date(v.created_at).toLocaleString("nb-NO", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </div>
                  </div>
                  <span className="chip">{v.status ?? "planlagt"}</span>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-3 text-lg font-semibold">Siste aktivitet</h2>
          {!isLoading && data && data.events.length === 0 ? (
            <div className="card p-8 text-center text-sm text-muted-foreground">
              Ingen aktivitet registrert enda.
            </div>
          ) : (
            <div className="card divide-y divide-border">
              {data?.events.map((e) => (
                <Link
                  key={e.id}
                  to="/projects/$id"
                  params={{ id: e.project_id }}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted"
                >
                  <Activity className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{e.title}</div>
                    {e.description && (
                      <div className="truncate text-xs text-muted-foreground">{e.description}</div>
                    )}
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {relTime(e.created_at)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
